import { useMutation } from 'react-query';
import quoterApiInstance from '../../api/quoterApiInstance';
import { MUTATION_KEYS } from '../../constants/reactQuery';
import { downloadBlob } from '../../api/pdfGeneratorApi';

const FALLBACK_PLAN_NAME = 'premium-table';

// Se remueven caracteres especiales (@, #, espacios, etc.) del nombre del plan, dejando sólo letras y números.
const sanitizePlanName = (planName) => (planName || '').replace(/[^\p{L}\p{N}]/gu, '');

// El nombre del archivo es el del template concatenado, p. ej. "test1premiumtable.pdf".
const buildFilename = (planName) => `PREMIUM_TABLE_${sanitizePlanName(planName) || FALLBACK_PLAN_NAME}.pdf`;

/**
 * GET /my-favorite-plans/:id/premium-table
 *
 * Descarga el PDF de la premium table de un plan. La respuesta es binaria
 * (`application/pdf`), por eso `responseType: 'blob'`. El nombre del archivo se arma con
 * el nombre del template (`<template> premium table.pdf`), no con el header del back.
 *
 * @param {object} vars
 * @param {string|number} vars.id        id del plan.
 * @param {string}        [vars.planName] nombre del template → nombre del archivo.
 * @returns {Promise<string>} el nombre de archivo con el que se descargó.
 */
const downloadPremiumTable = async ({ id, planName }) => {
    const locale = localStorage.getItem('locale') ?? 'en';
    const response = await quoterApiInstance.get(`/my-favorite-plans/${id}/premium-table`, {
        responseType: 'blob',
        headers: { 'Accept-Language': locale },
    });

    const blob = response.data instanceof Blob
        ? response.data
        : new Blob([response.data], { type: 'application/pdf' });

    const filename = buildFilename(planName);

    downloadBlob(blob, filename);
    return filename;
};

/**
 * Descarga el PDF de la premium table de un plan favorito.
 *
 * Es una mutación (no una query) porque es una acción puntual disparada desde la tabla,
 * sin resultado que cachear. Mientras corre, `mutation.variables.id` identifica la fila
 * en curso para poder mostrar el estado de carga sólo en ese botón.
 */
export const useDownloadFavoritePlanPremiumTable = (options = {}) => {
    return useMutation(
        MUTATION_KEYS.favoritePlans.premiumTable,
        (variables) => downloadPremiumTable(variables),
        {
            ...options,
            onError: (error, variables, context) => {
                console.error('Error downloading premium table PDF:', error);
                options.onError?.(error, variables, context);
            },
        }
    );
};

export default useDownloadFavoritePlanPremiumTable;
